import {
    BookingStatus,
    ClinicianCredential,
    DateFormats,
    IAvailabilitySchedule,
    IAvailabilityWindow,
    IClientBooking,
    IClinician,
    IIntakeAvailability,
    IExperienceLevelOption,
    ISearchParams,
    RecurrenceRule,
} from "intake_shared/src/types";
import * as React from "react";
import { AclContext } from "../../../../../contexts/AclContext";
import { CacheContext } from "../../../../../contexts/CacheContext";
import _ from "lodash";
import moment from "../../../../../utils/app_moment";
import { Button } from "../../../bits/Button/Button";
import store from "../../../../../store";
import TrashIcon from "../../../icons/Trash";
import styles from "./FilteredClinicians.module.scss";
import { Badge } from "../../../bits/Badge/Badge";
import { getV2BadgePropsByPractice } from "../../../../../constants";
import { cn } from "../../../../helpers";
import CredentialExpirationStatus from "../../CredentialExpirationStatus/CredentialExpirationStatus";

import BookingPauseDetails from "../../BookingPauseDetails/BookingPauseDetails";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

type TAvailabilityDateTime = IAvailabilitySchedule & IAvailabilityWindow;

interface FilterClinicianProps {
    filteredClinicians: IClinician[];
    searchParams: ISearchParams;
    setClinicianToBook: React.Dispatch<React.SetStateAction<IClinician>>;
    setSelectedApptToHoldId: React.Dispatch<React.SetStateAction<string>>;
    setAvailabilityToShow: React.Dispatch<React.SetStateAction<IAvailabilityWindow>>;
    runClinicianSearch: () => Promise<void>;
    setSelectedAppointmentFromCard: React.Dispatch<React.SetStateAction<boolean>>;
    allExperienceLevelOptions: IExperienceLevelOption[];
    withClientInfo?: boolean;
    // "grid" shows cards; "list" shows one clinician per full-width row
    layout?: "grid" | "list";
}

export default function FilteredClinicians({
    filteredClinicians,
    searchParams,
    setClinicianToBook,
    setSelectedApptToHoldId,
    setAvailabilityToShow,
    runClinicianSearch,
    setSelectedAppointmentFromCard,
    allExperienceLevelOptions,
    withClientInfo = false,
    layout = "grid",
}: FilterClinicianProps) {
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const olc = React.useContext(CacheContext);
    const [expandedCards, setExpandedCards] = React.useState<Set<string>>(
        () => new Set()
    );
    const toggleCard = (clinicianId: string) =>
        setExpandedCards((current) => {
            const next = new Set(current);
            if (next.has(clinicianId)) next.delete(clinicianId);
            else next.add(clinicianId);
            return next;
        });
    function popupClientBookingWindow(c: IClinician) {
        setClinicianToBook(c);
    }

    const availableServiceToShowOver4wks = [
        "MENTAL_HEALTH_ASSESSMENT:_FULLY_IN-PERSON_",
        "MENTAL_HEALTH_ASSESSMENT:_PARTIAL_IN-PERSON",
        "MENTAL_HEALTH_ASSESSMENT:_VIRTUAL",
    ];

    const shouldShowAvailabilitySlot = (d: TAvailabilityDateTime): boolean => {
        if (d.reservedFor) {
            return false;
        }
        const checkOneOffAppointmentDate = moment(
            (d.starting_date || d.start) && (d.end || d.ending_date)
        );
        const isBeforeCurrentDate = checkOneOffAppointmentDate.isBefore(moment());
        if (d.recurrenceRule === RecurrenceRule.ONE_OFF && isBeforeCurrentDate) {
            return false;
        }
        const startMoment = moment(d.start);
        const weeksAway = startMoment.diff(new Date(), "weeks");
        return (
            weeksAway <= 4 ||
            availableServiceToShowOver4wks.includes(d.availableService as string)
        );
    };

    const getVisibleAvailabilityCount = (
        dateTimes: TAvailabilityDateTime[]
    ): number => {
        return dateTimes.filter(shouldShowAvailabilitySlot).length;
    };

    const isVisibleIntakeSlot = (slot: IIntakeAvailability): boolean => {
        if (!slot.start_time) return false;
        const slotStart = moment(slot.start_time);
        if (!slotStart.isValid() || !slotStart.isAfter(moment())) return false;
        if (slot.booking_id || slot.client_id || slot.client_display_name)
            return false;
        return true;
    };

    const getVisibleIntakeAvailabilityCount = (
        slots: IIntakeAvailability[] = []
    ): number => {
        return slots.filter(isVisibleIntakeSlot).length;
    };

    const renderIntakeTimes = (
        slots: IIntakeAvailability[] = []
    ): JSX.Element | string => {
        const visibleSlots = _.sortBy(slots.filter(isVisibleIntakeSlot), (slot) =>
            moment(slot.start_time).valueOf()
        );

        if (visibleSlots.length < 1) {
            return "--";
        }

        return (
            <div className={styles.timeListing}>
                {visibleSlots.map((slot) => {
                    const start = moment(slot.start_time);
                    return (
                        <span
                            key={`intake-slot-${slot.id}`}
                            className={styles.intakeTimeSlot}
                        >
                            {start.format("ddd, MMM Do - h:mma")}
                        </span>
                    );
                })}
            </div>
        );
    };

    const flattenTimes = (
        dateTimes: TAvailabilityDateTime[],
        type: "available" | "unavailable" | "held" | "confirmed",
        clinician?: IClinician
    ): JSX.Element | null => {
        const datesByDay: { [key: string]: JSX.Element[] } = {
            0: [],
            1: [],
            2: [],
            3: [],
            4: [],
            5: [],
            6: [],
        };
        switch (type) {
            case "available": {
                if (dateTimes.length < 1) {
                    return <p>No availability</p>;
                }
                const availableDatesByDay = { ...datesByDay };
                _.sortBy(dateTimes, (d) => moment(d.start).hour()).forEach(
                    (d: TAvailabilityDateTime, idx) => {
                        // Only add button if it passes the condition
                        if (!shouldShowAvailabilitySlot(d)) {
                            return;
                        }

                        const startMoment = moment(d.start);
                        const weeksAway = startMoment.diff(new Date(), "weeks");
                        const dayIndex = startMoment.day();

                        availableDatesByDay[dayIndex].push(
                            <Button
                                key={`btn-avl-date-${idx}`}
                                onClick={() => {
                                    if (clinician) {
                                        setSelectedApptToHoldId(d.id);
                                        popupClientBookingWindow(clinician);
                                        setSelectedAppointmentFromCard(true);
                                    } else {
                                        setAvailabilityToShow(d);
                                    }
                                }}
                                className={styles.timeSlot}
                                variant="normal"
                                title="Hold this slot for the client"
                            >
                                {startMoment.format(
                                    startMoment.minute() > 0
                                        ? DateFormats.HOUR_MIN_FORMAT
                                        : DateFormats.HOUR_FORMAT
                                )}
                                {d.recurrenceRule === RecurrenceRule.BIWEEKLY
                                    ? " · Bi-weekly"
                                    : ""}
                                {(() => {
                                    const parts: string[] = [];
                                    if (d.location) {
                                        if (d.location === "Remote") {
                                            parts.push("Remote");
                                        } else {
                                            parts.push(d.location);
                                        }
                                    }
                                    if (weeksAway >= 1)
                                        parts.push(
                                            `${weeksAway} wk${
                                                weeksAway === 1 ? "" : "s"
                                            }`
                                        );
                                    if (d.ending_date)
                                        parts.push(
                                            `ends ${moment(d.ending_date).format(
                                                DateFormats.DAY_MONTH_FORMAT
                                            )}`
                                        );
                                    if (
                                        d.availableService &&
                                        (olc?.requestedServices || {})[
                                            d.availableService
                                        ]
                                    ) {
                                        if (
                                            (olc?.requestedServices || {})[
                                                d.availableService
                                            ] === "Teletherapy"
                                        ) {
                                            parts.push("Tele");
                                        } else if (
                                            (olc?.requestedServices || {})[
                                                d.availableService
                                            ] === "In-Person Therapy"
                                        ) {
                                            parts.push("In-Person");
                                        } else {
                                            parts.push(
                                                (olc?.requestedServices || {})[
                                                    d.availableService
                                                ]
                                            );
                                        }
                                    }

                                    return parts.length > 0
                                        ? ` · ${parts.join(" · ")}`
                                        : "";
                                })()}
                            </Button>
                        );
                    }
                );

                const hasVisibleSlots = Object.keys(availableDatesByDay).some(
                    (dayKey) => availableDatesByDay[dayKey].length > 0
                );

                if (!hasVisibleSlots) {
                    return <>--</>;
                }

                return (
                    <div className={styles.timeListing}>
                        {Object.keys(availableDatesByDay).map((dayKey, idx) => {
                            const dayNumber = parseInt(dayKey);
                            const dayNames = [
                                "Sun",
                                "Mon",
                                "Tue",
                                "Wed",
                                "Thu",
                                "Fri",
                                "Sat",
                            ];
                            const dayName = dayNames[dayNumber];

                            if (availableDatesByDay[dayKey].length === 0) {
                                return null;
                            }

                            return (
                                <div
                                    key={`day-${dayKey}`}
                                    className={styles.daySection}
                                >
                                    <div
                                        className={`${styles.dayLabel} ${
                                            styles[`day${dayNumber}`]
                                        }`}
                                    >
                                        {dayName}:
                                    </div>
                                    <div className={styles.dayTimeSlots}>
                                        {availableDatesByDay[dayKey]}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            }
            case "unavailable": {
                const upcomingOrCurrentVacations = dateTimes.filter(
                    (dt: TAvailabilityDateTime) => {
                        const startMoment = moment(dt.start).startOf("day");
                        const endMoment = moment(dt.end).endOf("day");
                        const today = moment().startOf("day");
                        const isOnVacation =
                            (today.isAfter(startMoment, "day") ||
                                today.isSame(startMoment, "day")) &&
                            today.isBefore(endMoment, "hours");
                        const hasUpcomingVacation = today.isBefore(
                            startMoment,
                            "days"
                        );
                        return isOnVacation || hasUpcomingVacation;
                    }
                );
                if (upcomingOrCurrentVacations.length) {
                    const vacationList = _.orderBy(
                        upcomingOrCurrentVacations,
                        (u) => moment(u.start).toISOString(),
                        "asc"
                    ).reduce((prev, curr) => {
                        const vacationStartMoment = moment(curr.start);
                        const vacationEndMoment = moment(curr.end);
                        prev = `${prev} ${vacationStartMoment.format(
                            DateFormats.DAY_MONTH_FORMAT
                        )}-${vacationEndMoment.format(
                            DateFormats.DAY_MONTH_FORMAT
                        )}, `;
                        return prev;
                    }, "");
                    return <>{vacationList}</>;
                }
                return <>No upcoming vacations</>;
            }
            case "held": {
                if (dateTimes.length < 1) {
                    return <>No held appointments</>;
                }

                return (
                    <>
                        {(dateTimes as IClientBooking[]).map((d) => {
                            const startMoment = moment(d.start);
                            return (
                                <div key={`key-${d.id}`} className="flex">
                                    <p>
                                        {d.client_display_name} (
                                        {startMoment.format(
                                            startMoment.minute() > 0
                                                ? DateFormats.DAY_TIME_FORMAT
                                                : DateFormats.DAY_HOUR_FORMAT
                                        )}
                                        )
                                    </p>
                                    <Button
                                        onClick={async () => {
                                            await store.deleteAppoinment({
                                                clinicianId: d.clinician_id,
                                                clientId: d.client_id,
                                                appointmentId: d.id,
                                                cancelledBy:
                                                    loggedInUserEmail || "Intake",
                                                status: BookingStatus.HELD,
                                                lastUpdatedBy: loggedInUserEmail!,
                                            });
                                            await runClinicianSearch();
                                        }}
                                        variant="normal"
                                        size="icon"
                                        title="Remove this hold"
                                        aria-label="Remove this hold"
                                    >
                                        <TrashIcon />
                                    </Button>
                                </div>
                            );
                        })}
                    </>
                );
            }
            default: {
                return <p></p>;
            }
        }
    };

    const categoryMapping = allExperienceLevelOptions.reduce((acc, option) => {
        acc[option.value] = option.category;
        return acc;
    }, {});

    return (
        <div
            className={cn(styles.filteredClinicians, {
                [styles.listLayout]: layout === "list",
            })}
        >
            {_.sortBy(filteredClinicians, "firstname").map((c, idx) => {
                const searchedExperienceLevels = Object.keys(
                    searchParams.experience_level
                );

                const sortedKeys = Object.keys(c.experience_levels)
                    .filter((k) => categoryMapping[k] !== "Provider Style")
                    .sort((a, b) => {
                        const categoryA = categoryMapping[a] || "Other";
                        const categoryB = categoryMapping[b] || "Other";

                        // Prioritize non-"Licensing" categories first
                        if (categoryA === "Licensing" && categoryB !== "Licensing")
                            return 1;
                        if (categoryB === "Licensing" && categoryA !== "Licensing")
                            return -1;

                        return a.localeCompare(b);
                    });

                const clinicianAge = moment().diff(c.dob, "years", false);
                const yearsOfPractice = moment().diff(
                    c.started_practicing,
                    "years",
                    false
                );

                const locations = c.office_windows?.map((w) => w.location);
                const uniqueLocations = Array.from(new Set(locations));

                const visibleIntakeSessionCount = getVisibleIntakeAvailabilityCount(
                    c.available_for_intake_sessions ?? []
                );

                const bookedIds = new Set([
                    ...(c.confirmed || []).map((b) => b.id),
                    ...(c.held || []).map((b) => b.id),
                ]);
                const unBookedAvailable = (c.available || []).filter(
                    (a) => !bookedIds.has(a.id)
                );

                const visibleExpLevelKeys = sortedKeys.filter(
                    (k) =>
                        (searchedExperienceLevels.length === 0 ||
                            searchedExperienceLevels.includes(k)) &&
                        k !== "Young Adults (18-25)" &&
                        k !== "EMDR"
                );

                const cardKey = c.id ?? `card-${idx}`;
                const showDetails = expandedCards.has(`${cardKey}:details`);
                const hasSearchedExpLevels = searchedExperienceLevels.length > 0;
                const isProvisionalCredential = [
                    ClinicianCredential.MHCLP,
                    ClinicianCredential.LMSW,
                ].includes(c.credential);
                const supervisorNames = [
                    c.supervisorFirstName && c.supervisorLastName
                        ? `${c.supervisorFirstName} ${c.supervisorLastName}`
                        : "",
                    ...(c.secondary_supervisor_ids || []).map(
                        (supervisor) =>
                            `${supervisor.supervisor_first_name} ${supervisor.supervisor_last_name}`
                    ),
                ]
                    .filter(Boolean)
                    .join(", ");

                return (
                    <div
                        key={`prof-${idx}`}
                        className={cn(styles.clinicianCard, {
                            [styles.listRow]: layout === "list",
                        })}
                    >
                        <div className={styles.clinicianInfoHeader}>
                            <div className={styles.clinicianDetails}>
                                <h3 className={styles.clinicianName}>
                                    {c.fullname}
                                </h3>
                                <div className={styles.clinicianBadges}>
                                    {c.booking_paused && (
                                        <OverlayTrigger
                                            placement="bottom"
                                            trigger={["hover", "focus"]}
                                            overlay={
                                                <Tooltip
                                                    id={`booking-pause-${
                                                        c.id ?? c.email
                                                    }`}
                                                >
                                                    <BookingPauseDetails
                                                        clinician={c}
                                                    />
                                                </Tooltip>
                                            }
                                        >
                                            <span
                                                tabIndex={0}
                                                aria-label="Do Not Book: pause details"
                                            >
                                                <Badge
                                                    tone="danger"
                                                    content="Do Not Book"
                                                />
                                            </span>
                                        </OverlayTrigger>
                                    )}
                                    {c.marked_as_priority && (
                                        <Badge tone="success" content="Priority" />
                                    )}

                                    <CredentialExpirationStatus
                                        clinician={c}
                                        experienceLevelOptions={
                                            allExperienceLevelOptions
                                        }
                                    />

                                    {c.credential && (
                                        <Badge
                                            content={c.credential}
                                            tone={
                                                isProvisionalCredential
                                                    ? "warning"
                                                    : "info"
                                            }
                                        />
                                    )}

                                    {c.departments?.map((d) => (
                                        <Badge
                                            key={d}
                                            content={d?.replaceAll("_", " ")}
                                            textColor={
                                                getV2BadgePropsByPractice(d)
                                                    .textColor
                                            }
                                            bgColor={
                                                getV2BadgePropsByPractice(d).bgColor
                                            }
                                        />
                                    ))}
                                </div>
                                {supervisorNames && (
                                    <span
                                        className={styles.supervisorSummary}
                                        title={`Supervisor(s): ${supervisorNames}`}
                                    >
                                        <span
                                            className={styles.supervisorSummaryLabel}
                                        >
                                            Supervisor:
                                        </span>{" "}
                                        {supervisorNames}
                                    </span>
                                )}
                            </div>

                            <p className={styles.clinicianFacts}>
                                <span>Age {clinicianAge}</span>
                                <span>
                                    {yearsOfPractice}{" "}
                                    {yearsOfPractice === 1 ? "yr" : "yrs"} practicing
                                </span>
                                <span>Joined {moment(c.start_date).fromNow()}</span>
                                {c.contact_info_client && (
                                    <span title="Client contact">
                                        {c.contact_info_client}
                                    </span>
                                )}
                            </p>
                        </div>

                        <div className={styles.clinicianContent}>
                            <div
                                className={cn(
                                    styles.contentSection,
                                    styles.notesSection
                                )}
                            >
                                <div className={styles.sectionLabel}>Notes</div>
                                <div
                                    className={cn(styles.sectionValue, {
                                        [styles.clamped]: !showDetails,
                                    })}
                                    title={
                                        showDetails
                                            ? undefined
                                            : c.personality_notes || undefined
                                    }
                                >
                                    {c.personality_notes || "--"}
                                </div>
                            </div>

                            <div
                                className={cn(
                                    styles.contentSection,
                                    styles.availableSection
                                )}
                            >
                                <div className={styles.availableLabel}>
                                    Available times (
                                    {getVisibleAvailabilityCount(unBookedAvailable)})
                                </div>
                                <div className={styles.availableContent}>
                                    {unBookedAvailable.length > 0
                                        ? flattenTimes(
                                              unBookedAvailable,
                                              "available",
                                              { ...c, available: unBookedAvailable }
                                          )
                                        : "--"}
                                </div>
                                {layout === "list" && (
                                    <Button
                                        variant="normal"
                                        size="auto"
                                        className={styles.detailsToggle}
                                        onClick={() =>
                                            toggleCard(`${cardKey}:details`)
                                        }
                                        aria-expanded={showDetails}
                                    >
                                        {showDetails
                                            ? "Fewer details"
                                            : "More details"}
                                    </Button>
                                )}
                            </div>

                            {visibleIntakeSessionCount > 0 && (
                                <div
                                    className={cn(
                                        styles.contentSection,
                                        styles.availableSection
                                    )}
                                >
                                    <div className={styles.availableLabel}>
                                        Intake sessions ({visibleIntakeSessionCount})
                                    </div>
                                    {renderIntakeTimes(
                                        c.available_for_intake_sessions || []
                                    )}
                                </div>
                            )}

                            {(layout === "grid" || showDetails) && (
                                <div className={styles.detailsArea}>
                                    <div
                                        className={cn(
                                            styles.contentSectionRow,
                                            layout === "grid" &&
                                                c.methodology?.length > 0 &&
                                                styles.cardDetailsRow
                                        )}
                                    >
                                        <div className={styles.contentSection}>
                                            <div className={styles.sectionLabel}>
                                                Locations
                                            </div>
                                            <div className={styles.sectionValue}>
                                                {uniqueLocations.length > 0
                                                    ? uniqueLocations.join(", ")
                                                    : "--"}
                                            </div>
                                        </div>

                                        <div className={styles.contentSection}>
                                            <div className={styles.sectionLabel}>
                                                Vacation
                                            </div>
                                            <div className={styles.sectionValue}>
                                                {flattenTimes(
                                                    c.unavailable,
                                                    "unavailable"
                                                )}
                                            </div>
                                        </div>

                                        {layout === "grid" &&
                                            c.methodology?.length > 0 && (
                                                <div
                                                    className={styles.contentSection}
                                                >
                                                    <div
                                                        className={
                                                            styles.sectionLabel
                                                        }
                                                    >
                                                        Methodology
                                                    </div>
                                                    <div
                                                        className={
                                                            styles.sectionValue
                                                        }
                                                    >
                                                        {[...c.methodology]
                                                            .sort()
                                                            .join(", ")}
                                                    </div>
                                                </div>
                                            )}
                                    </div>

                                    {layout === "list" &&
                                        c.methodology?.length > 0 && (
                                            <div className={styles.contentSection}>
                                                <div className={styles.sectionLabel}>
                                                    Methodology
                                                </div>
                                                <div className={styles.sectionValue}>
                                                    {[...c.methodology]
                                                        .sort()
                                                        .join(", ")}
                                                </div>
                                            </div>
                                        )}
                                </div>
                            )}

                            {(layout === "grid" ||
                                (layout === "list" && showDetails) ||
                                hasSearchedExpLevels) && (
                                <div
                                    className={cn(
                                        styles.experienceLevelsContainer,
                                        styles.detailsArea
                                    )}
                                >
                                    <div className={styles.sectionLabel}>
                                        Experience levels (
                                        {visibleExpLevelKeys.length})
                                    </div>
                                    <div
                                        className={cn(styles.experienceLevels, {
                                            [styles.withClientInfo]: withClientInfo,
                                        })}
                                    >
                                        {visibleExpLevelKeys.map((k, idx) => {
                                            const exp_level = c.experience_levels[k];
                                            const levelClass = exp_level.do_not_want
                                                ? styles.experienceLevelNot
                                                : exp_level.level === "High" ||
                                                  exp_level.level === "yes"
                                                ? styles.experienceLevelHigh
                                                : exp_level.level === "Medium"
                                                ? styles.experienceLevelMedium
                                                : exp_level.level === "Low" ||
                                                  exp_level.level === "no"
                                                ? styles.experienceLevelLow
                                                : "";

                                            return (
                                                <div
                                                    key={`exp-p-${idx}--${k}`}
                                                    className={cn(
                                                        styles.experienceLevel,
                                                        levelClass
                                                    )}
                                                >
                                                    <span
                                                        className={
                                                            styles.experienceLabel
                                                        }
                                                        title={k}
                                                    >
                                                        {k}
                                                    </span>
                                                    <span
                                                        className={styles.levelValue}
                                                    >
                                                        {exp_level.level || "--"}
                                                        {exp_level.certified
                                                            ? ` (Certified: ${exp_level.certified})`
                                                            : ""}
                                                        {exp_level.do_not_want
                                                            ? " (DNB)"
                                                            : ""}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
